import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Settings, Save, DollarSign, Wallet, Calendar, Percent, Upload, X, Image } from 'lucide-react';
import { settingsApi } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

export default function AdminSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const bep20QrRef = useRef(null);
  const trc20QrRef = useRef(null);
  const bannerRef = useRef(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await settingsApi.get();
      setSettings(response.data.settings);
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await settingsApi.update(settings);
      toast.success('Settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const toggleDate = (date) => {
    const dates = settings.withdrawal_dates || [];
    if (dates.includes(date)) {
      setSettings({
        ...settings,
        withdrawal_dates: dates.filter(d => d !== date)
      });
    } else {
      setSettings({
        ...settings,
        withdrawal_dates: [...dates, date].sort((a, b) => a - b)
      });
    }
  };

  const handleQrUpload = (e, network) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error('File size must be less than 2MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result;
        if (network === 'bep20') {
          setSettings({...settings, usdt_bep20_qr: base64});
        } else {
          setSettings({...settings, usdt_trc20_qr: base64});
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleBannerUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) {
      toast.error('Banner image must be less than 3MB');
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setSettings({ ...settings, dashboard_banner: reader.result });
      toast.success('Banner uploaded — click Save Settings to apply');
    };
    reader.readAsDataURL(file);
  };

  const removeQr = (network) => {
    if (network === 'bep20') {
      setSettings({...settings, usdt_bep20_qr: ''});
      if (bep20QrRef.current) bep20QrRef.current.value = '';
    } else {
      setSettings({...settings, usdt_trc20_qr: ''});
      if (trc20QrRef.current) trc20QrRef.current.value = '';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-amber-500/20 border-t-amber-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-settings">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-zinc-500/10">
            <Settings className="w-6 h-6 text-zinc-400" />
          </div>
          <div>
            <h1 className="text-2xl text-white font-medium">Platform Settings</h1>
            <p className="text-sm text-zinc-500">Configure platform parameters</p>
          </div>
        </div>

        <Button onClick={handleSave} disabled={saving} className="gold-btn" data-testid="save-settings-btn">
          {saving ? (
            <div className="w-5 h-5 border-2 border-black/20 border-t-black rounded-full animate-spin" />
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" /> Save Settings
            </>
          )}
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* BLC Price */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card p-6"
        >
          <div className="flex items-center gap-2 mb-4">
            <DollarSign className="w-5 h-5 text-amber-400" />
            <h2 className="text-white font-medium">BLC Price</h2>
          </div>
          <div>
            <label className="text-xs text-zinc-400 mb-2 block">Current Price (USD)</label>
            <Input
              type="number"
              step="0.001"
              value={settings?.blc_price || 0}
              onChange={(e) => setSettings({...settings, blc_price: parseFloat(e.target.value)})}
              className="bg-black/50 border-white/10"
              data-testid="blc-price-input"
            />
          </div>
        </motion.div>

        {/* Fees */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="glass-card p-6"
        >
          <div className="flex items-center gap-2 mb-4">
            <Percent className="w-5 h-5 text-amber-400" />
            <h2 className="text-white font-medium">Fee Settings</h2>
          </div>
          <div className="space-y-4">
            <div>
              <label className="text-xs text-zinc-400 mb-2 block">Withdrawal Fee (%)</label>
              <Input
                type="number"
                step="0.1"
                value={settings?.withdrawal_fee || 0}
                onChange={(e) => setSettings({...settings, withdrawal_fee: parseFloat(e.target.value)})}
                className="bg-black/50 border-white/10"
              />
            </div>
            <div>
              <label className="text-xs text-zinc-400 mb-2 block">Transfer Fee (%)</label>
              <Input
                type="number"
                step="0.1"
                value={settings?.transfer_fee || 0}
                onChange={(e) => setSettings({...settings, transfer_fee: parseFloat(e.target.value)})}
                className="bg-black/50 border-white/10"
              />
            </div>
            <div>
              <label className="text-xs text-zinc-400 mb-2 block">Deposit Fee (%)</label>
              <Input
                type="number"
                step="0.1"
                value={settings?.deposit_fee || 0}
                onChange={(e) => setSettings({...settings, deposit_fee: parseFloat(e.target.value)})}
                className="bg-black/50 border-white/10"
              />
            </div>
          </div>
        </motion.div>

        {/* Dashboard Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="glass-card p-6 md:col-span-2"
        >
          <div className="flex items-center gap-2 mb-4">
            <Image className="w-5 h-5 text-amber-400" />
            <h2 className="text-white font-medium">Dashboard Popup Banner</h2>
          </div>
          <p className="text-xs text-zinc-500 mb-4">
            Upload a banner image that will be shown as a popup to users when they first visit their dashboard (once per session). Delete to stop showing.
          </p>

          {settings?.dashboard_banner ? (
            <div className="space-y-4">
              <div className="relative mx-auto max-w-lg">
                <img
                  src={settings.dashboard_banner}
                  alt="Dashboard Banner"
                  className="w-full h-auto rounded-xl border border-white/10 shadow-lg"
                />
                <button
                  onClick={() => {
                    setSettings({ ...settings, dashboard_banner: '' });
                    if (bannerRef.current) bannerRef.current.value = '';
                  }}
                  className="absolute -top-2 -right-2 p-1.5 bg-red-500 rounded-full text-white hover:bg-red-600 transition-colors"
                  data-testid="delete-banner-btn"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <p className="text-center text-xs text-green-400">Banner is active. Users will see this on their first dashboard visit each session.</p>
            </div>
          ) : (
            <div
              onClick={() => bannerRef.current?.click()}
              className="border-2 border-dashed border-white/10 rounded-xl p-8 text-center cursor-pointer hover:border-amber-500/50 transition-colors max-w-lg mx-auto"
              data-testid="upload-banner-area"
            >
              <input
                ref={bannerRef}
                type="file"
                accept="image/*"
                onChange={handleBannerUpload}
                className="hidden"
                data-testid="banner-file-input"
              />
              <Upload className="w-10 h-10 text-zinc-500 mx-auto mb-3" />
              <p className="text-sm text-zinc-400">Click to upload banner image</p>
              <p className="text-xs text-zinc-600 mt-1">Recommended: 800x400px or similar. Max 3MB. JPG/PNG/WebP</p>
            </div>
          )}
        </motion.div>

        {/* Payment Methods with QR Codes */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="glass-card p-6 md:col-span-2"
        >
          <div className="flex items-center gap-2 mb-4">
            <Wallet className="w-5 h-5 text-amber-400" />
            <h2 className="text-white font-medium">Payment Methods</h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            {/* BEP-20 */}
            <div className="space-y-4 p-4 rounded-lg bg-black/30">
              <h3 className="text-white font-medium text-sm">USDT BEP-20 (BSC)</h3>
              <div>
                <label className="text-xs text-zinc-400 mb-2 block">Wallet Address</label>
                <Input
                  value={settings?.usdt_bep20_address || ''}
                  onChange={(e) => setSettings({...settings, usdt_bep20_address: e.target.value})}
                  placeholder="0x..."
                  className="bg-black/50 border-white/10 font-mono text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-zinc-400 mb-2 block">QR Code</label>
                {settings?.usdt_bep20_qr ? (
                  <div className="relative w-32 h-32 mx-auto">
                    <img 
                      src={settings.usdt_bep20_qr} 
                      alt="BEP20 QR" 
                      className="w-full h-full object-contain rounded-lg border border-white/10"
                    />
                    <button
                      onClick={() => removeQr('bep20')}
                      className="absolute -top-2 -right-2 p-1 bg-red-500 rounded-full text-white hover:bg-red-600"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <div 
                    onClick={() => bep20QrRef.current?.click()}
                    className="border-2 border-dashed border-white/10 rounded-lg p-4 text-center cursor-pointer hover:border-amber-500/50 transition-colors"
                  >
                    <input
                      ref={bep20QrRef}
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleQrUpload(e, 'bep20')}
                      className="hidden"
                    />
                    <Upload className="w-6 h-6 text-zinc-500 mx-auto mb-2" />
                    <p className="text-xs text-zinc-400">Upload QR Code</p>
                  </div>
                )}
              </div>
            </div>

            {/* TRC-20 */}
            <div className="space-y-4 p-4 rounded-lg bg-black/30">
              <h3 className="text-white font-medium text-sm">USDT TRC-20 (TRON)</h3>
              <div>
                <label className="text-xs text-zinc-400 mb-2 block">Wallet Address</label>
                <Input
                  value={settings?.usdt_trc20_address || ''}
                  onChange={(e) => setSettings({...settings, usdt_trc20_address: e.target.value})}
                  placeholder="T..."
                  className="bg-black/50 border-white/10 font-mono text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-zinc-400 mb-2 block">QR Code</label>
                {settings?.usdt_trc20_qr ? (
                  <div className="relative w-32 h-32 mx-auto">
                    <img 
                      src={settings.usdt_trc20_qr} 
                      alt="TRC20 QR" 
                      className="w-full h-full object-contain rounded-lg border border-white/10"
                    />
                    <button
                      onClick={() => removeQr('trc20')}
                      className="absolute -top-2 -right-2 p-1 bg-red-500 rounded-full text-white hover:bg-red-600"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <div 
                    onClick={() => trc20QrRef.current?.click()}
                    className="border-2 border-dashed border-white/10 rounded-lg p-4 text-center cursor-pointer hover:border-amber-500/50 transition-colors"
                  >
                    <input
                      ref={trc20QrRef}
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleQrUpload(e, 'trc20')}
                      className="hidden"
                    />
                    <Upload className="w-6 h-6 text-zinc-500 mx-auto mb-2" />
                    <p className="text-xs text-zinc-400">Upload QR Code</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Withdrawal Rules */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="glass-card p-6"
        >
          <div className="flex items-center gap-2 mb-4">
            <Settings className="w-5 h-5 text-amber-400" />
            <h2 className="text-white font-medium">Withdrawal Rules</h2>
          </div>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-zinc-400 mb-2 block">Min Withdrawal ($)</label>
                <Input
                  type="number"
                  value={settings?.min_withdrawal || 0}
                  onChange={(e) => setSettings({...settings, min_withdrawal: parseFloat(e.target.value)})}
                  className="bg-black/50 border-white/10"
                />
              </div>
              <div>
                <label className="text-xs text-zinc-400 mb-2 block">Max Withdrawal ($)</label>
                <Input
                  type="number"
                  value={settings?.max_withdrawal || 0}
                  onChange={(e) => setSettings({...settings, max_withdrawal: parseFloat(e.target.value)})}
                  className="bg-black/50 border-white/10"
                />
              </div>
            </div>
          </div>
        </motion.div>

        {/* ROI Distribution Time */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="glass-card p-6"
        >
          <div className="flex items-center gap-2 mb-4">
            <Calendar className="w-5 h-5 text-amber-400" />
            <h2 className="text-white font-medium">ROI Distribution</h2>
          </div>
          <div className="space-y-4">
            <div>
              <label className="text-xs text-zinc-400 mb-2 block">Distribution Time (UTC)</label>
              <Input
                type="time"
                value={settings?.roi_distribution_time || '00:00'}
                onChange={(e) => setSettings({...settings, roi_distribution_time: e.target.value})}
                className="bg-black/50 border-white/10"
              />
              <p className="text-xs text-zinc-500 mt-2">ROI is distributed to ROI Wallet daily at this time</p>
            </div>
            <div>
              <label className="text-xs text-zinc-400 mb-2 block">ROI Start Delay (Hours)</label>
              <Input
                type="number"
                min="0"
                value={settings?.roi_start_delay_hours ?? 24}
                onChange={(e) => setSettings({...settings, roi_start_delay_hours: parseInt(e.target.value) || 0})}
                className="bg-black/50 border-white/10"
              />
              <p className="text-xs text-zinc-500 mt-2">
                First ROI distribution starts after this many hours from staking. 
                <span className="text-amber-400 ml-1">(Set to 0 for immediate ROI)</span>
              </p>
            </div>
          </div>
        </motion.div>

        {/* Withdrawal Dates */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="glass-card p-6 md:col-span-2"
        >
          <div className="flex items-center gap-2 mb-4">
            <Calendar className="w-5 h-5 text-amber-400" />
            <h2 className="text-white font-medium">Withdrawal Dates</h2>
          </div>
          <p className="text-xs text-zinc-500 mb-4">Select the days of the month when withdrawals and ROI transfers are allowed.</p>
          <div className="grid grid-cols-7 gap-2">
            {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
              <button
                key={day}
                onClick={() => toggleDate(day)}
                className={`py-2 rounded-lg text-sm font-medium transition-colors ${
                  settings?.withdrawal_dates?.includes(day)
                    ? 'bg-amber-500 text-black'
                    : 'glass text-zinc-400 hover:text-white'
                }`}
              >
                {day}
              </button>
            ))}
          </div>
          <p className="text-xs text-zinc-500 mt-4">
            Selected dates: {settings?.withdrawal_dates?.length > 0 
              ? settings.withdrawal_dates.join(', ') 
              : 'All days allowed'}
          </p>
        </motion.div>
      </div>
    </div>
  );
}
